import { useNavigate } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import Popover, { type AnchorPosition } from '../ui/Popover';
import Icon from '../ui/Icon';
import type { Folder, Tag } from '../../types/api';
import { useCurrentUser } from '../../services/userHooks';
import { useToast } from '../../hooks/useToast';
import { useDeleteFolder, useConvertFolderToTag } from '../../services/folders';
import { useDeleteTag } from '../../services/tagService';
import { useSidebarItemModals } from '../../hooks/useSidebarItemModals';

interface SidebarItemMenuProps {
  /** Pass exactly one of `folder` or `tag`. */
  folder?: Folder;
  tag?: Tag;
  /** True when this row is the folder/tag currently being viewed. When it is
   *  deleted or converted, the user is redirected home. */
  isActive?: boolean;
  /** Viewport coordinate to anchor the menu at; the menu is open while non-null. */
  anchorPosition: AnchorPosition | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Right-click context menu shared by sidebar folder and tag rows. Reuses the
 * same Edit / Convert to Tag / Delete actions as the header list-options menu.
 */
function SidebarItemMenu({
  folder,
  tag,
  isActive = false,
  anchorPosition,
  onOpenChange,
}: SidebarItemMenuProps) {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const { showToast } = useToast();
  const { editFolder, editTag } = useSidebarItemModals();
  const deleteFolderMutation = useDeleteFolder();
  const convertToTagMutation = useConvertFolderToTag();
  const deleteTagMutation = useDeleteTag();

  const close = () => onOpenChange(false);

  const handleOpenInNewTab = () => {
    // Restores the native right-click "open in new tab" we suppress by taking
    // over the context menu. Mirrors the row's Link target.
    const path = folder
      ? `/folder/${encodeURIComponent(folder.slug)}`
      : tag
        ? `/tags/${encodeURIComponent(tag.slug)}`
        : null;
    if (path) window.open(path, '_blank', 'noopener,noreferrer');
    close();
  };

  const handleEdit = () => {
    if (folder) editFolder(folder);
    else if (tag) editTag(tag);
    close();
  };

  const handleDeleteFolder = async () => {
    if (!folder || !currentUser?.formKey) return;
    if (
      !confirm(
        t`Are you sure you want to delete this folder? All articles will be moved to your home.`,
      )
    ) {
      return;
    }

    try {
      await deleteFolderMutation.mutateAsync({
        folderId: folder.id,
        formKey: currentUser.formKey,
      });
      if (isActive) navigate({ to: '/home' });
      close();
    } catch (err) {
      console.error('Failed to delete folder:', err);
    }
  };

  const handleConvertToTag = async () => {
    if (!folder || !currentUser?.formKey) return;
    if (
      !confirm(
        t`When you convert this folder to a tag...\n* Articles will be given a new tag with this folder name.\n* Articles will be moved to Archive.\n* This folder will be deleted on completion.\n\nThis cannot be undone. Would you like to continue?`,
      )
    ) {
      return;
    }

    try {
      await convertToTagMutation.mutateAsync({
        folderId: folder.id,
        formKey: currentUser.formKey,
      });
      showToast(
        t`Folder conversion in progress... it can take a few minutes for conversion to complete.`,
        'success',
        6000,
      );
      if (isActive) navigate({ to: '/home' });
      close();
    } catch (err) {
      console.error('Failed to convert folder to tag:', err);
    }
  };

  const handleDeleteTag = async () => {
    if (!tag) return;
    if (!confirm(t`Are you sure you want to delete this tag?`)) {
      return;
    }

    try {
      await deleteTagMutation.mutateAsync({ tagId: tag.id });
      if (isActive) navigate({ to: '/home' });
      close();
    } catch (err) {
      console.error('Failed to delete tag:', err);
    }
  };

  return (
    <Popover
      open={anchorPosition !== null}
      onOpenChange={onOpenChange}
      anchorPosition={anchorPosition}
      // Align the menu's left edge to the cursor so it opens to the
      // right of it, rather than centered on the click point.
      placement="bottom-start"
      offset={{ mainAxis: 4, crossAxis: 2 }}
      role="menu"
      preventCloseOnContentClick
      content={
        <div className="min-w-48">
          <button onClick={handleOpenInNewTab} className="menu-item">
            <span className="menu-item-icon">
              <Icon name="external-link" />
            </span>
            <span>
              <Trans>Open in new tab</Trans>
            </span>
          </button>
          {folder ? (
            <>
              <button onClick={handleEdit} className="menu-item">
                <span className="menu-item-icon">
                  <Icon name="edit" />
                </span>
                <span>
                  <Trans>Edit Folder</Trans>
                </span>
              </button>
              <button
                onClick={handleConvertToTag}
                disabled={convertToTagMutation.isPending}
                className="menu-item"
              >
                <span className="menu-item-icon">
                  <Icon name="tag" />
                </span>
                <span>
                  {convertToTagMutation.isPending ? (
                    <Trans>Converting...</Trans>
                  ) : (
                    <Trans>Convert to Tag</Trans>
                  )}
                </span>
              </button>
              <button
                onClick={handleDeleteFolder}
                disabled={deleteFolderMutation.isPending}
                className="menu-item text-danger hover:text-copy-inverse hover:bg-surface-danger-hover"
              >
                <span className="menu-item-icon">
                  <Icon name="trash" />
                </span>
                <span>
                  {deleteFolderMutation.isPending ? (
                    <Trans>Deleting...</Trans>
                  ) : (
                    <Trans>Delete Folder</Trans>
                  )}
                </span>
              </button>
            </>
          ) : (
            <>
              <button onClick={handleEdit} className="menu-item">
                <span className="menu-item-icon">
                  <Icon name="edit" />
                </span>
                <span>
                  <Trans>Edit tag</Trans>
                </span>
              </button>
              <button
                onClick={handleDeleteTag}
                disabled={deleteTagMutation.isPending}
                className="menu-item text-danger hover:text-copy-inverse hover:bg-surface-danger-hover"
              >
                <span className="menu-item-icon">
                  <Icon name="trash" />
                </span>
                <span>
                  {deleteTagMutation.isPending ? (
                    <Trans>Deleting...</Trans>
                  ) : (
                    <Trans>Delete Tag</Trans>
                  )}
                </span>
              </button>
            </>
          )}
        </div>
      }
    >
      {/* Invisible trigger: the menu is opened programmatically on right-click
          and anchored to the cursor, so this element is never interacted with. */}
      <span aria-hidden className="hidden" />
    </Popover>
  );
}

export default SidebarItemMenu;
